import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const unmergedButtonVariants = cva(
  [
    "ds-btn-bg",
    "inline-flex items-center justify-center overflow-clip",
    "border border-solid border-ds-border-btn",
    "font-dm-sans font-medium text-footnote whitespace-nowrap text-center",
    "transition-[background-image] duration-150 ease-out",
    "cursor-pointer select-none",
    "disabled:cursor-not-allowed disabled:border-transparent disabled:text-ds-slate-400",
    "focus-visible:outline-none focus-visible:shadow-focus-ring",
    "transition-opacity",
  ],
  {
    variants: {
      variant: {
        primary: [
          "[--ds-btn-base:var(--color-ds-primary)]",
          "[--ds-btn-overlay:transparent]",
          "[--ds-btn-overlay-hover:rgba(251,249,245,0.24)]",
          "[--ds-btn-overlay-active:rgba(251,249,245,0.24)]",
          "border-ds-cream/32",
          "text-ds-on-primary",
          "disabled:text-ds-slate-500",
        ],
        secondary: [
          "[--ds-btn-base:var(--color-ds-secondary)]",
          "[--ds-btn-overlay:transparent]",
          "[--ds-btn-overlay-hover:rgba(11,17,16,0.04)]",
          "[--ds-btn-overlay-active:rgba(11,17,16,0.04)]",
          "text-ds-on-secondary",
          "border-ds-border-selected",
        ],
        accent: [
          "[--ds-btn-base:#eef822]",
          "[--ds-btn-overlay:transparent]",
          "[--ds-btn-overlay-hover:rgba(251,249,245,0.18)]",
          "[--ds-btn-overlay-active:rgba(251,249,245,0.18)]",
          "text-ds-lime-950",
        ],
        destructive: [
          "[--ds-btn-base:#df1f1f]",
          "[--ds-btn-overlay:transparent]",
          "[--ds-btn-overlay-hover:rgba(251,249,245,0.18)]",
          "[--ds-btn-overlay-active:rgba(251,249,245,0.18)]",
          "text-ds-strawberry-50",
        ],
        ghost: [
          "[--ds-btn-base:transparent]",
          "[--ds-btn-overlay:transparent]",
          "[--ds-btn-overlay-hover:rgba(11,17,16,0.04)]",
          "[--ds-btn-overlay-active:rgba(11,17,16,0.04)]",
          "text-ds-mint-900",
          "border-transparent",
        ],
        outline: [
          "[--ds-btn-base:var(--color-ds-cream)]",
          "[--ds-btn-overlay:transparent]",
          "[--ds-btn-overlay-hover:rgba(11,17,16,0.04)]",
          "[--ds-btn-overlay-active:rgba(11,17,16,0.04)]",
          "text-ds-mint-900",
          "border-[rgba(11,17,16,0.06)]",
          "focus-visible:border-transparent",
        ],
      },
      size: {
        lg: "h-14 rounded-xl",
        md: "h-12 rounded-lg",
        sm: "h-9 rounded-md",
        xs: "h-6 rounded text-sm px-2 py-0",
        icon: "size-10 rounded-lg",
      },
      mode: {
        default: "px-4 py-2 gap-2 [&>svg]:size-4",
        iconOnly: "[&>svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      mode: "default",
    },
  }
);

/**
 * The classes of a variant. Each variant restates a base class (the outline's
 * border colour over the base's cream one), so the two are merged here: raw,
 * both would ship and the stylesheet's order would pick one. A `<Link>` or a
 * bare `<button>` styled with this then draws the button `<Button>` draws.
 */
function buttonVariants(
  props: VariantProps<typeof unmergedButtonVariants>
): string {
  return cn(unmergedButtonVariants(props));
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof unmergedButtonVariants> & {
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
  };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, mode, leftIcon, rightIcon, children, ...props },
  ref
) {
  const resolvedMode = mode ?? (children == null ? "iconOnly" : "default");

  return (
    <button
      ref={ref}
      className={cn(
        unmergedButtonVariants({ variant, size, mode: resolvedMode }),
        className
      )}
      {...props}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
});

export { Button, buttonVariants, type ButtonProps };
